/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { forwardRef, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap, cx, jsx } from '@atlaskit/css';
import StopwatchIcon from '@atlaskit/icon/core/stopwatch';
import { Popup } from '@atlaskit/popup/popup';
import type { TriggerProps } from '@atlaskit/popup/types';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import TooltipPrimitive, { type TooltipPrimitiveProps } from '@atlaskit/tooltip/TooltipPrimitive';

import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../../controllers/analytics';
import type { NounKeys } from '../../../../constants/nouns';
import type { ProductKeys } from '../../../../constants/product-keys';
import { onFilterOptionTimeoutButtonClicked } from '../../../../utils/events/filter-option-timeout-button-clicked';
import { messages } from './messages';

type FilterOptionTimeoutProps = {
	option: {
		label: React.ReactNode;
		value: ProductKeys | NounKeys | undefined;
		onSelect?: () => void;
	};
	setIsMouseOver: (isMouseOver: boolean) => void;
};

const styles = cssMap({
	trigggerContainer: {
		// A bit of a hack to right align stopwatch icon with the result counts
		marginRight: token('space.negative.050'),
	},
	tooltip: {
		boxSizing: 'border-box',
		maxWidth: 'fit-content',
		backgroundColor: token('color.background.neutral.bold'),
		borderRadius: token('radius.small', '3px'),
		color: token('color.text.inverse'),
		font: token('font.body.small'),
		insetBlockStart: token('space.0'),
		insetInlineStart: token('space.0'),
		overflowWrap: 'break-word',
		paddingBlockEnd: token('space.050'),
		paddingBlockStart: token('space.050'),
		paddingInlineEnd: token('space.075'),
		paddingInlineStart: token('space.075'),
		wordWrap: 'break-word',
	},
	popupContainer: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		borderRadius: token('radius.small'),
		display: 'flex',
		flexDirection: 'column',
	},
	popupPrimaryText: {
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		alignSelf: 'flex-start',
	},
	popupSecondaryText: {
		font: token('font.body'),
		fontWeight: token('font.weight.regular'),
		alignSelf: 'flex-start',
		marginTop: token('space.025'),
		marginBottom: token('space.025'),
	},
	buttonContainer: {
		alignSelf: 'flex-end',
		marginTop: token('space.200'),
	},
});

const TimeoutTooltip: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<TooltipPrimitiveProps> & React.RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, TooltipPrimitiveProps>(function CustomTooltip(
	{ children, className, ...rest },
	ref,
) {
	return (
		<TooltipPrimitive
			{...rest}
			// These prop values come from the ADS docs for customising the tooltip component
			// https://atlassian.design/components/tooltip/examples
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides, @atlaskit/ui-styling-standard/no-classname-prop
			className={className}
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
			css={styles.tooltip}
			ref={ref}
		>
			{children}
		</TooltipPrimitive>
	);
});

export const FilterOptionTimeout: ({
	option,
	setIsMouseOver,
}: FilterOptionTimeoutProps) => JSX.Element = ({
	option,
	setIsMouseOver,
}: FilterOptionTimeoutProps) => {
	const { formatMessage } = useIntl();

	const [isOpen, setIsOpen] = useState(false);

	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	const trigger = (triggerProps: TriggerProps) => {
		return (
			<Box xcss={cx(styles.trigggerContainer)}>
				<Tooltip
					component={TimeoutTooltip}
					content={formatMessage(messages.timeoutTooltip, { product: option.label })}
					position="bottom"
				>
					<IconButton
						testId={`search-page-filter-option-timeout-${option.value}`}
						icon={StopwatchIcon}
						appearance="subtle"
						spacing="compact"
						label={formatMessage(messages.timeoutTooltip, { product: option.label })}
						isSelected={isOpen}
						onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
							event.stopPropagation();
							setIsOpen(!isOpen);
						}}
						{...triggerProps}
					/>
				</Tooltip>
			</Box>
		);
	};

	const content = () => {
		return (
			<Box
				xcss={cx(styles.popupContainer)}
				onPointerDown={(e: React.PointerEvent<HTMLDivElement>) => e.stopPropagation()}
			>
				<Box xcss={cx(styles.popupPrimaryText)}>
					{formatMessage(messages.timeoutPrimaryText, { product: option.label })}
				</Box>
				<Box xcss={cx(styles.popupSecondaryText)}>
					{formatMessage(messages.timeoutSecondaryText, { product: option.label })}
				</Box>
				<Box xcss={cx(styles.buttonContainer)}>
					<Button
						testId={`search-page-filter-option-timeout-button-${option.value}`}
						appearance="primary"
						onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
							event.stopPropagation();
							setIsOpen(false);
							option.onSelect?.();

							setIsMouseOver(false);

							fireAnalyticsEvent(
								onFilterOptionTimeoutButtonClicked({
									commonAttributes,
									nonPrivacySafeAttributes,
									product: option.value as ProductKeys,
								}),
							);
						}}
					>
						{formatMessage(messages.timeoutButtonText, { product: option.label })}
					</Button>
				</Box>
			</Box>
		);
	};

	return (
		<Popup
			isOpen={isOpen}
			content={content}
			trigger={trigger}
			placement="bottom-end"
			onClose={() => setIsOpen(false)}
		/>
	);
};
